import { Box, Grid, MenuItem, Tooltip, Typography } from "@mui/material";
import { SubscriptionContext } from "contexts/Subscription";
import { Dispatch, SetStateAction, useContext } from "react";
import { UpgradeDialogSource } from "schemas/dashboard";
import { Map } from "schemas/functions";

import DropdownSelect from "components/DropdownSelect";
import { ON_BEHALF_OF_TOKEN } from "utils/localStorage";
import { trackEvent } from "utils/tracking";
import { useSearchBrand } from "utils/useSearchBrand";

import SubgroupMenuItem from "./Subgroup";
import { Option, Subgroup } from "./schema";

interface Props {
  label: string;
  options: Option[];
  params: string;
  iconsMap?: Map;
  setClose: Dispatch<SetStateAction<boolean>>;
  close: boolean;
  isOptionSelected: (key: string, params: string) => boolean;
  isSmallScreen: boolean;
  selectedOptionIcon: JSX.Element;
  subgroups?: Subgroup[];
}

const SelectFilter = ({
  label,
  options,
  params,
  iconsMap,
  setClose,
  close,
  isOptionSelected,
  isSmallScreen,
  selectedOptionIcon,
  subgroups,
}: Props) => {
  const { addCategoryQueryToSearch } = useSearchBrand();
  const { subscription, setUpgradeDialogSource } =
    useContext(SubscriptionContext);

  const handleAddOption = (
    name: string,
    value: string,
    paidOnly: boolean = false,
  ) => {
    if (paidOnly && !subscription?.isSubscribed) {
      trackEvent("Restricted Filter Option Clicked", {
        "Filter Type": name,
        "Filter Name": value,
      });
      setUpgradeDialogSource(UpgradeDialogSource.restricted_filtering_feature);
      return;
    }
    addCategoryQueryToSearch(name, value);
    setClose(true);
  };

  const getIcon = (key: string, isLock: boolean) => {
    if (isLock) {
      return "🔒";
    }
    if (iconsMap && key in iconsMap) {
      return iconsMap[key];
    }
  };

  const isAdmin =
    Boolean(sessionStorage.getItem(ON_BEHALF_OF_TOKEN)) ||
    process.env.NODE_ENV === "development";

  const groupedKeys = new Set(subgroups?.flatMap((sg) => sg.keys) ?? []);

  const displayOptions = (
    isAdmin ? options : options.filter((option) => !option.adminOnly)
  ).filter((option) => !groupedKeys.has(option.key));

  return (
    <DropdownSelect
      isSmallScreen={isSmallScreen}
      label={label}
      id={label}
      close={close}
      setClose={setClose}
    >
      {displayOptions.map((option) => (
        <MenuItem
          dense
          key={option.key}
          onClick={() => handleAddOption(params, option.key, option.paidOnly)}
        >
          <Tooltip title={option.label} placement="right">
            <Grid container alignItems="center">
              {isOptionSelected(option.key, params) && selectedOptionIcon}
              <Box sx={{ mr: 1 }}>
                {getIcon(
                  option.key,
                  Boolean(option.paidOnly && !subscription?.isSubscribed),
                )}
              </Box>
              <Typography>{option.name}</Typography>
            </Grid>
          </Tooltip>
        </MenuItem>
      ))}
      {subgroups?.map((subgroup) => {
        const subgroupOptions = (
          isAdmin ? options : options.filter((o) => !o.adminOnly)
        ).filter((o) => subgroup.keys.includes(o.key));

        return (
          <SubgroupMenuItem
            key={subgroup.label}
            subgroup={subgroup}
            options={subgroupOptions}
            params={params}
            iconsMap={iconsMap}
            isOptionSelected={isOptionSelected}
            selectedOptionIcon={selectedOptionIcon}
            onSelect={handleAddOption}
            getIcon={getIcon}
            subscription={subscription}
          />
        );
      })}
    </DropdownSelect>
  );
};

export default SelectFilter;
